import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Badge,
  Card,
  CardContent,
  DataTable,
  Input,
  LeafletMap,
  PageHeader,
  Pagination,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  toast,
  type Column,
  type LeafletPin,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Station } from '@eon/types';
import { Eye, MoreHorizontal, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import { StationMapPopup } from '@/components/StationMapPopup';
import { stationCoordinates, stationMapVariant } from '@/lib/station-map';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export function StationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labels = usePaginationLabels();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useAdminStations({ q, page, pageSize, ...sortParams });
  const mapList = hooks.useAdminStations({ page: 1, pageSize: 200 });
  const del = hooks.useDeleteStation();

  const onDelete = (s: Station) => {
    if (!window.confirm(`"${s.name}"`)) return;
    del.mutate(s.id, {
      onSuccess: () => toast.success(t('actions.delete')),
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  const columns: Column<Station>[] = [
    {
      key: 'name',
      header: t('station.name'),
      sortable: true,
      cell: (s) => (
        <Link
          to="/stations/$stationId"
          params={{ stationId: s.id }}
          className="font-medium hover:underline"
        >
          {s.name}
        </Link>
      ),
    },
    { key: 'address', header: t('station.address'), sortable: true, cell: (s) => s.address ?? '—' },
    { key: 'city', header: t('station.detail.city'), sortable: true, cell: (s) => s.city ?? '—' },
    { key: 'devices', header: t('station.devices'), cell: (s) => s.deviceCount },
    {
      key: 'status',
      header: t('station.status'),
      cell: (s) => (
        <Badge variant={s.active ? 'success' : 'secondary'}>
          {s.active ? t('common.yes') : t('common.no')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12 text-right',
      cell: (s) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t('common.actions')}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => navigate({ to: '/stations/$stationId', params: { stationId: s.id } })}
            >
              <Eye className="mr-2 h-4 w-4" /> {t('station.detail.view')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                navigate({ to: '/stations/$stationId/edit', params: { stationId: s.id } })
              }
            >
              <Pencil className="mr-2 h-4 w-4" /> {t('actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onDelete(s)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> {t('actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const pins: LeafletPin[] = useMemo(() => {
    const next: LeafletPin[] = [];
    for (const s of mapList.data?.data ?? []) {
      const coords = stationCoordinates(s);
      if (!coords) continue;
      next.push({
        id: s.id,
        lat: coords.lat,
        lng: coords.lng,
        variant: stationMapVariant(s),
        popup: <StationMapPopup station={s} />,
      });
    }
    return next;
  }, [mapList.data?.data]);

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = list.data?.totalPages ?? 1;

  return (
    <>
      <PageHeader
        title={t('nav.stations')}
        description={list.data ? `${totalCount.toLocaleString()} ${t('nav.stations')}` : undefined}
        actions={
          <>
            <Button asChild>
              <Link to="/stations/new">
                <Plus className="mr-2 h-4 w-4" /> {t('station.create.new')}
              </Link>
            </Button>
            <div className="relative w-full sm:w-auto">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder={t('actions.search')}
                className="w-full pl-9 sm:w-64"
              />
            </div>
          </>
        }
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          <DataTable
            data={list.data?.data}
            columns={columns}
            loading={list.isLoading}
            rowKey={(s) => s.id}
            sort={sort}
            onSortChange={onSortChange}
          />
          {list.data && totalCount > 0 ? (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              loading={list.isFetching}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              labels={labels}
            />
          ) : null}
        </div>
        <Card>
          <CardContent className="p-2">
            <LeafletMap pins={pins} height={420} fitToPins className="overflow-hidden rounded-md" />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
